import { useState } from 'react';
import { resolveAssetUrl, type CatalogueItem } from '../api/client';
import { DownloadBar } from './DownloadBar';
import { useLanguage } from '../lib/language';

export interface CatalogueCardProps {
  item: CatalogueItem;
  onDelete?: (id: string) => void;
}

export function CatalogueCard(props: CatalogueCardProps) {
  const { item, onDelete } = props;
  // v7 — open state for the read-only annotation popover so the user can
  // re-view hotspots saved on a catalogue item.
  const [openAnnotationId, setOpenAnnotationId] = useState<string | null>(null);
  const { lang } = useLanguage();
  return (
    <article className="group overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm motion-safe:transition-shadow hover:shadow-md dark:border-neutral-800 dark:bg-neutral-900">
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-neutral-100 dark:bg-neutral-800">
        <img
          src={resolveAssetUrl(item.thumbnail_url || item.image_url)}
          alt={describeCatalogueItem(item)}
          loading="lazy"
          className="h-full w-full object-cover motion-safe:transition-transform motion-safe:group-hover:scale-[1.02]"
        />
        {/* v5 — compact DownloadBar pinned to the top-right of the image.
            Always visible (high-contrast chip) so the icons remain
            discoverable on touch and keyboard. */}
        <div className="absolute right-2 top-2 z-10">
          <DownloadBar
            variant="compact"
            imageId={item.generated_id}
            catalogueId={item.id}
            title={item.title || item.notes || 'Saved reference'}
            hideSetAsStarting
          />
        </div>
        {/* v7 — read-only annotation hotspots on saved eyes. Click to view
            the note. No edit / remove affordance — saved eyes are read-only. */}
        {item.annotations && item.annotations.length > 0 ? (
          <div className="pointer-events-none absolute inset-0">
            {item.annotations.map((a) => {
              const isOpen = openAnnotationId === a.id;
              return (
                <div
                  key={a.id}
                  className="pointer-events-auto absolute"
                  style={{
                    left: `${a.x * 100}%`,
                    top: `${a.y * 100}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setOpenAnnotationId(isOpen ? null : a.id)}
                    aria-label={
                      a.text
                        ? `Annotation: ${a.text}`
                        : 'Empty annotation'
                    }
                    className="block h-5 w-5 cursor-pointer rounded-full border-2 border-accent bg-accent/30 shadow-[0_0_0_1px_rgba(0,0,0,0.4)] hover:bg-accent/45 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  />
                  {isOpen ? (
                    <div
                      role="dialog"
                      aria-label="Annotation note"
                      className="absolute left-1/2 top-full z-20 mt-2 w-48 -translate-x-1/2 rounded-md border border-neutral-300 bg-white p-2 text-xs shadow-lg dark:border-neutral-700 dark:bg-neutral-900"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <p className="whitespace-pre-wrap text-neutral-800 dark:text-neutral-100">
                        {a.text || (
                          <span className="italic text-neutral-500 dark:text-neutral-400">
                            {lang === 'plain' ? '(empty note)' : '— empty —'}
                          </span>
                        )}
                      </p>
                      <p className="mt-1 text-[10px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                        {new Date(a.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
      <div className="flex flex-col gap-2 p-3">
        <h3 className="line-clamp-1 text-sm font-semibold text-neutral-900 dark:text-neutral-50">
          {item.title ?? 'Untitled reference'}
        </h3>
        {item.notes ? (
          <p className="line-clamp-2 text-xs text-neutral-600 dark:text-neutral-400">
            {item.notes}
          </p>
        ) : null}
        {item.tags.length > 0 ? (
          <ul className="flex flex-wrap gap-1">
            {item.tags.map((tag) => (
              <li
                key={tag}
                className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300"
              >
                {tag}
              </li>
            ))}
          </ul>
        ) : null}
        <div className="mt-1 flex items-center justify-between gap-2">
          <time
            dateTime={item.created_at}
            className="text-[10px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400"
          >
            {new Date(item.created_at).toLocaleDateString()}
          </time>
          {onDelete ? (
            <button
              type="button"
              onClick={() => onDelete(item.id)}
              aria-label={`Delete ${item.title ?? 'reference'}`}
              className="inline-flex h-10 min-w-[44px] cursor-pointer items-center justify-center rounded-md px-3 text-xs font-medium text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
            >
              Delete
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}

/**
 * Synthesise a meaningful alt text from a saved catalogue item so AT
 * users hear title + tags instead of a generic placeholder.
 */
function describeCatalogueItem(item: CatalogueItem): string {
  const parts: string[] = ['Saved eye reference'];
  if (item.title) parts.push(`titled "${item.title}"`);
  if (item.tags.length > 0) parts.push(`tagged ${item.tags.join(', ')}`);
  if (item.notes) parts.push(`notes: ${item.notes}`);
  return parts.join(', ');
}
